(() => {
  const GLOBAL_KEY = '__faCountrySelector';

  if (window[GLOBAL_KEY]?.scan) {
    window[GLOBAL_KEY].scan();
    return;
  }

  const state = { active: null, scan, remount };
  window[GLOBAL_KEY] = state;

  function getHeaderIcons() {
    return document.querySelector('sticky-header .header__icons, .section-header .header__icons, .header__icons');
  }

  function removeDuplicateTriggers(activeTrigger) {
    document.querySelectorAll('.fa-country-trigger').forEach((node) => {
      if (node !== activeTrigger) node.remove();
    });
  }

  function mount(instance) {
    if (!instance || instance.destroyed || !instance.trigger) return;
    const { trigger } = instance;
    removeDuplicateTriggers(trigger);
    const icons = getHeaderIcons();
    if (!icons) return;

    if (trigger.parentElement !== icons) {
      const search = icons.querySelector('.header__search');
      const account = icons.querySelector('.header__icon--account');
      if (search) search.insertAdjacentElement('afterend', trigger);
      else if (account) account.insertAdjacentElement('beforebegin', trigger);
      else icons.prepend(trigger);
    }
    trigger.dataset.faCountryMounted = 'true';
  }

  function destroy(instance, removeTrigger = true) {
    if (!instance || instance.destroyed) return;
    instance.destroyed = true;
    instance.controller?.abort();
    if (instance.modal && !instance.modal.hidden) {
      instance.modal.hidden = true;
      document.body.classList.remove('fa-country-modal-open');
    }
    if (removeTrigger && instance.trigger) instance.trigger.remove();
  }

  function init(root) {
    if (!root) return;
    if (state.active?.root === root && !state.active.destroyed) {
      mount(state.active);
      return;
    }
    if (state.active) {
      destroy(state.active, true);
      state.active = null;
    }

    const trigger = root.querySelector('[data-fa-country-trigger]');
    const modal = root.querySelector('[data-fa-country-modal]');
    const searchInput = root.querySelector('[data-fa-country-search]');
    const closeButton = root.querySelector('[data-fa-country-close]');
    const bestGroup = root.querySelector('[data-fa-best-group]');
    const restGroup = root.querySelector('[data-fa-rest-group]');
    const restList = root.querySelector('[data-fa-rest-list]');
    const searchGroup = root.querySelector('[data-fa-search-group]');
    const searchList = root.querySelector('[data-fa-search-list]');
    const emptyState = root.querySelector('[data-fa-country-empty]');
    const countryIndex = root.querySelector('[data-fa-country-index]');

    if (!trigger || !modal || !searchInput || !closeButton || !bestGroup || !restGroup || !restList || !searchGroup || !searchList || !emptyState || !countryIndex) return;

    let restCountries = [];
    try {
      const parsed = JSON.parse(countryIndex.textContent || '[]');
      if (Array.isArray(parsed)) {
        restCountries = parsed.filter((country) => country?.name && country?.code && country?.url);
      }
    } catch (_error) {
      restCountries = [];
    }

    const bestCountries = Array.from(root.querySelectorAll('[data-fa-best-list] a[data-fa-country-code]'))
      .map((link) => ({
        name: link.dataset.faCountryName || link.textContent.trim(),
        code: link.dataset.faCountryCode || '',
        url: link.href,
        row: link.closest('li'),
      }))
      .filter((country) => country.name && country.code && country.url && country.row);

    const countries = [
      ...bestCountries,
      ...restCountries.map((country) => ({
        name: String(country.name),
        code: String(country.code),
        url: String(country.url),
        row: null,
      })),
    ];

    const controller = new AbortController();
    const { signal } = controller;
    const source = root.dataset.navSource || 'fa-my';
    let previouslyFocused = null;
    let restReady = false;

    const instance = { root, trigger, modal, controller, sectionId: root.dataset.sectionId || '', destroyed: false };
    state.active = instance;
    root.dataset.initialized = 'true';

    const buildCountryRow = (country) => {
      if (country.row) return country.row.cloneNode(true);

      const item = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'fa-country-link';
      link.href = country.url;
      link.dataset.faCountryCode = country.code;
      link.dataset.faCountryName = country.name;
      link.setAttribute('aria-label', `Flower delivery to ${country.name}`);

      const flag = document.createElement('img');
      flag.src = `https://flagcdn.com/${country.code.toLowerCase()}.svg`;
      flag.alt = `Flag of ${country.name}`;
      flag.width = 26;
      flag.height = 18;
      flag.loading = 'lazy';
      flag.decoding = 'async';

      const label = document.createElement('span');
      label.textContent = country.name;

      link.append(flag, label);
      item.append(link);
      return item;
    };

    const ensureRestList = () => {
      if (restReady) return;
      const fragment = document.createDocumentFragment();
      countries.filter((country) => !country.row).forEach((country) => fragment.append(buildCountryRow(country)));
      restList.replaceChildren(fragment);
      restReady = true;
    };

    const filterCountries = (term) => {
      const query = term.trim().toLowerCase();
      if (!query) {
        ensureRestList();
        bestGroup.hidden = false;
        restGroup.hidden = false;
        searchGroup.hidden = true;
        searchList.replaceChildren();
        emptyState.hidden = true;
        return;
      }

      const matches = countries.filter(
        (country) => country.name.toLowerCase().includes(query) || country.code.toLowerCase().includes(query),
      );
      bestGroup.hidden = true;
      restGroup.hidden = true;
      searchGroup.hidden = false;

      const fragment = document.createDocumentFragment();
      matches.forEach((country) => fragment.append(buildCountryRow(country)));
      searchList.replaceChildren(fragment);
      emptyState.hidden = matches.length !== 0;
    };

    const getFocusable = () =>
      Array.from(modal.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'))
        .filter((element) => !element.hidden && element.offsetParent !== null);

    const open = () => {
      previouslyFocused = document.activeElement;
      modal.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('fa-country-modal-open');
      searchInput.value = '';
      filterCountries('');
      requestAnimationFrame(() => searchInput.focus({ preventScroll: true }));
    };

    const close = () => {
      if (modal.hidden) return;
      modal.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('fa-country-modal-open');
      if (previouslyFocused?.isConnected) previouslyFocused.focus?.({ preventScroll: true });
    };

    const trackCountryNavigation = (country) => {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'fa_country_navigation',
          navigation_source: source,
          source_market: 'MY',
          destination_country: country.name,
          destination_code: country.code,
        });
      } catch (_error) {}
    };

    trigger.addEventListener('click', open, { signal });
    closeButton.addEventListener('click', close, { signal });
    searchInput.addEventListener('input', (event) => filterCountries(event.currentTarget.value), { signal });

    modal.addEventListener('click', (event) => {
      const countryLink = event.target.closest?.('a[data-fa-country-code]');
      if (countryLink) {
        const country = countries.find((item) => item.code === countryLink.dataset.faCountryCode);
        if (country) trackCountryNavigation(country);
        return;
      }
      if (event.target === modal) close();
    }, { signal });

    document.addEventListener('keydown', (event) => {
      if (modal.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = getFocusable();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }, { signal });

    mount(instance);
  }

  function scan() {
    const roots = Array.from(document.querySelectorAll('[data-fa-country-root]'));
    if (!roots.length) {
      if (state.active && !state.active.root?.isConnected) {
        destroy(state.active, true);
        state.active = null;
      }
      return;
    }
    const currentRoot = state.active && roots.includes(state.active.root) ? state.active.root : roots[0];
    init(currentRoot);
    if (state.active) {
      removeDuplicateTriggers(state.active.trigger);
      mount(state.active);
    }
  }

  function remount() {
    scan();
  }

  function onSectionLoad() {
    requestAnimationFrame(remount);
  }

  function onSectionUnload(event) {
    const sectionId = event.detail?.sectionId || '';
    const unloadedRoot = event.target?.matches?.('[data-fa-country-root]')
      ? event.target
      : event.target?.querySelector?.('[data-fa-country-root]');
    if (state.active && ((sectionId && sectionId === state.active.sectionId) || (unloadedRoot && unloadedRoot === state.active.root))) {
      destroy(state.active, true);
      state.active = null;
    }
  }

  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  window.addEventListener('pageshow', remount);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
  else scan();
})();
